/**
 * MetafieldPicker — BLU-460 follow-up (PLAN §5.2 + §6.1).
 *
 * Combobox-style dropdown that surfaces Shopify metafields actually present
 * on the store: formal `metafieldDefinitions` for BOTH owner types (PRODUCT
 * and, since BLU-1018, PRODUCTVARIANT) plus informal metafields harvested
 * off products. Selecting a row fills the `namespace` + `key` inputs in one
 * click; a "Use custom namespace" footer drops back to free-text inputs.
 *
 * Both owner types land in one "Defined" group on purpose: a definition is
 * picked by namespace + key, and the PURPOSE (not this list) decides which
 * owner the sync reads. The remaining gap the manual escape hatch covers is
 * a VARIANT field with no formal definition, which the product-only informal
 * harvest still cannot see.
 *
 * Five visual states wired off the props passed by the parent Card:
 *   - `loading`       — discovery query in flight; skeleton placeholder.
 *   - `loaded`        — ≥1 row available; show the trigger + popover.
 *   - `empty`         — discovery resolved with 0 rows; collapse to free
 *                       text inputs with helper copy.
 *   - `errored`       — discovery query rejected; free text + Retry link.
 *   - `custom-mode`   — operator explicitly clicked "Use custom namespace";
 *                       free text inputs visible until they hit "Pick from
 *                       list" to return.
 *
 * A variant-scoped purpose also carries a permanent note under the field
 * saying so. It is NOT tied to a discovery state: an admin whose store has
 * plenty of product metafields still needs to know this particular field is
 * read off the VARIANT.
 *
 * Never blocks the operator: the free-text inputs are ALWAYS reachable via
 * the custom-mode toggle, even in `loaded` state. The dropdown is a
 * SHORTCUT, never a constraint (PLAN §10 risks table — "the dropdown never
 * blocks").
 *
 * Microcopy: curly quotes, no em-dashes, sentence-case button labels
 * (DESIGN.md §17 + flawless-typography skill).
 *
 * Every `<button>` carries `type="button"` explicitly — this component
 * renders inside the parent OrganizationSettings.tsx form (PLAN §9.1
 * v3.1 nested-form audit).
 */

import { useEffect, useMemo, useState } from 'react';
import { Check, ChevronsUpDown, ExternalLink, Loader2, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
// BLU-536 / Phase 56.1 Plan 07 — purpose metadata is single-sourced in
// `./purposes` (which itself derives the union from @threadheads/shared).
// No local purpose unions or per-purpose ternaries in this file.
import {
  PURPOSE_KEY_PLACEHOLDER,
  VARIANT_PURPOSES,
  type Purpose,
} from './purposes';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface DiscoveredMetafieldRow {
  namespace: string;
  key: string;
  type: string | null;
  source: 'definition' | 'product';
  exampleProductGid?: string;
  // `exampleValue` is intentionally NOT included on the wire — see backend
  // `DiscoveredMetafield` type for the rationale (latent PII risk if a
  // tenant uses `customer_notes`-style metafields).
  productOccurrences?: number;
}

export type DiscoveryStatus = 'loading' | 'loaded' | 'empty' | 'errored';

interface MetafieldPickerProps {
  purpose: Purpose;
  /** Current namespace value (controlled). */
  namespace: string;
  /** Current key value (controlled). */
  keyValue: string;
  /** Whether the input is editable (admins true, coordinators false). */
  editable: boolean;
  /** Whether the row is currently mid-mutation; disables inputs. */
  busy: boolean;
  /** Set true to flag the input as invalid (mirrors `aria-invalid`). */
  namespaceInvalid: boolean;
  keyInvalid: boolean;
  /** Discovery query state from the parent. */
  discoveryStatus: DiscoveryStatus;
  /** Discovered rows (any source). Empty when status is `empty`/`errored`/`loading`. */
  discoveredRows: ReadonlyArray<DiscoveredMetafieldRow>;
  /** Manual retry when discovery errored. */
  onRetryDiscovery: () => void;
  /** Set namespace + key together when a row is selected. */
  onPick: (namespace: string, key: string) => void;
  /** Free-text edits while in `custom-mode`/`empty`/`errored`. */
  onNamespaceChange: (value: string) => void;
  onKeyChange: (value: string) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MetafieldPicker({
  purpose,
  namespace,
  keyValue,
  editable,
  busy,
  namespaceInvalid,
  keyInvalid,
  discoveryStatus,
  discoveredRows,
  onRetryDiscovery,
  onPick,
  onNamespaceChange,
  onKeyChange,
}: MetafieldPickerProps) {
  const [open, setOpen] = useState(false);
  const [customMode, setCustomMode] = useState(false);
  // Track whether customMode was set by the operator clicking the
  // "Custom field" escape vs auto-set by discovery returning empty/errored.
  // BLU-498 review fix — when discovery recovers (retry success) we want to
  // auto-reset back to dropdown mode UNLESS the operator made an explicit
  // choice to use custom mode.
  const [customModeExplicit, setCustomModeExplicit] = useState(false);

  // Sync custom-mode to discovery state — when discovery errors or returns
  // empty, the dropdown trigger isn't useful, so always show free-text
  // inputs. When discovery transitions back to 'loaded' with results, the
  // dropdown becomes useful again; auto-reset unless the operator explicitly
  // chose custom mode.
  useEffect(() => {
    if (discoveryStatus === 'empty' || discoveryStatus === 'errored') {
      setCustomMode(true);
      return;
    }
    if (
      discoveryStatus === 'loaded' &&
      discoveredRows.length > 0 &&
      !customModeExplicit
    ) {
      setCustomMode(false);
    }
  }, [discoveryStatus, discoveredRows.length, customModeExplicit]);

  // Two sections: formal definitions first, informal product rows second.
  // Each section sorted by namespace then key on the server; we preserve
  // order here.
  const { definitionRows, productRows } = useMemo(() => {
    const defs: DiscoveredMetafieldRow[] = [];
    const prods: DiscoveredMetafieldRow[] = [];
    for (const row of discoveredRows) {
      if (row.source === 'definition') {
        defs.push(row);
      } else {
        prods.push(row);
      }
    }
    return { definitionRows: defs, productRows: prods };
  }, [discoveredRows]);

  const showDropdown =
    discoveryStatus === 'loading' || (discoveryStatus === 'loaded' && !customMode);

  // Match the active selection to a discovered row so the dropdown trigger
  // can render a "checked" indicator.
  const activeRowId = `${namespace}.${keyValue}`;

  return (
    <div className="space-y-2">
      {/* Group heading — NOT an htmlFor target. The free-text Namespace and
          Key inputs below have their own scoped Labels; an htmlFor here
          would collide with the Namespace input and cause SR label
          concatenation. */}
      <span className="text-xs font-medium text-foreground">Shopify field</span>

      {showDropdown && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              aria-label="Select a Shopify field"
              aria-expanded={open}
              disabled={!editable || busy || discoveryStatus === 'loading'}
              className="w-full justify-between font-mono"
            >
              {discoveryStatus === 'loading' ? (
                <span className="inline-flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                  Loading fields&hellip;
                </span>
              ) : namespace && keyValue ? (
                <span>
                  {namespace}.{keyValue}
                </span>
              ) : (
                <span className="text-muted-foreground">Select a Shopify field</span>
              )}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search&hellip;" />
              <CommandList>
                <CommandEmpty>No matches.</CommandEmpty>

                {definitionRows.length > 0 && (
                  <CommandGroup heading="Defined">
                    {definitionRows.map((row) => {
                      const id = `${row.namespace}.${row.key}`;
                      return (
                        <CommandItem
                          key={`def-${id}`}
                          value={id}
                          onSelect={() => {
                            onPick(row.namespace, row.key);
                            setOpen(false);
                          }}
                        >
                          <Check
                            className={`mr-2 h-4 w-4 ${
                              activeRowId === id ? 'opacity-100' : 'opacity-0'
                            }`}
                            aria-hidden="true"
                          />
                          <span className="font-mono">{id}</span>
                          {row.type && (
                            <span className="ml-auto text-xs text-muted-foreground">
                              {row.type}
                            </span>
                          )}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                )}

                {productRows.length > 0 && (
                  <CommandGroup heading="Found on products">
                    {productRows.map((row) => {
                      const id = `${row.namespace}.${row.key}`;
                      return (
                        <CommandItem
                          key={`prod-${id}`}
                          value={id}
                          onSelect={() => {
                            onPick(row.namespace, row.key);
                            setOpen(false);
                          }}
                        >
                          <Check
                            className={`mr-2 h-4 w-4 ${
                              activeRowId === id ? 'opacity-100' : 'opacity-0'
                            }`}
                            aria-hidden="true"
                          />
                          <span className="font-mono">{id}</span>
                          {row.productOccurrences != null && (
                            <span className="ml-auto text-xs text-muted-foreground tabular-nums">
                              found on {row.productOccurrences} product
                              {row.productOccurrences === 1 ? '' : 's'}
                            </span>
                          )}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                )}

                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => {
                      setCustomMode(true);
                      setCustomModeExplicit(true);
                      setOpen(false);
                    }}
                  >
                    Use custom namespace
                  </CommandItem>
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}

      {!showDropdown && (
        <div className="space-y-2 rounded-md border bg-muted/20 p-3">
          {discoveryStatus === 'errored' && (
            <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
              <span>
                Couldn&rsquo;t reach Shopify. Enter the namespace and key
                manually.
              </span>
              <button
                type="button"
                onClick={onRetryDiscovery}
                className="inline-flex items-center gap-1 text-xs text-foreground underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded"
              >
                <RotateCcw className="h-3 w-3" aria-hidden="true" />
                Retry
              </button>
            </div>
          )}
          {discoveryStatus === 'empty' && (
            <p className="text-xs text-muted-foreground">
              No metafields found on your products or variants yet. Enter the
              namespace and key manually.
            </p>
          )}
          {customMode && discoveryStatus === 'loaded' && (
            <div className="flex items-center justify-end">
              <button
                type="button"
                onClick={() => {
                  setCustomMode(false);
                  // Reset explicit flag so a future discovery error
                  // → recovery cycle auto-restores dropdown mode.
                  setCustomModeExplicit(false);
                }}
                className="text-xs text-muted-foreground underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded"
              >
                Pick from list
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor={`${purpose}-namespace`} className="text-xs">
                Namespace
              </Label>
              <Input
                id={`${purpose}-namespace`}
                value={namespace}
                onChange={(e) => onNamespaceChange(e.target.value.trim().toLowerCase())}
                placeholder="my_fields"
                disabled={!editable || busy}
                aria-invalid={namespaceInvalid}
                aria-describedby={`${purpose}-namespace-help`}
                className="font-mono"
              />
              <p
                id={`${purpose}-namespace-help`}
                className="text-xs text-muted-foreground"
              >
                The Shopify field group (usually{' '}
                <span className="font-mono">my_fields</span>).
              </p>
              {namespaceInvalid && (
                <p className="text-xs text-destructive">
                  Use lowercase letters, numbers, and underscores. Start with
                  a letter.
                </p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor={`${purpose}-key`} className="text-xs">
                Key
              </Label>
              <Input
                id={`${purpose}-key`}
                value={keyValue}
                onChange={(e) => onKeyChange(e.target.value.trim().toLowerCase())}
                placeholder={PURPOSE_KEY_PLACEHOLDER[purpose]}
                disabled={!editable || busy}
                aria-invalid={keyInvalid}
                aria-describedby={`${purpose}-key-help`}
                className="font-mono"
              />
              <p id={`${purpose}-key-help`} className="text-xs text-muted-foreground">
                The Shopify field name.
              </p>
              {keyInvalid && (
                <p className="text-xs text-destructive">
                  Use lowercase letters, numbers, and underscores. Start with
                  a letter.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/*
        BLU-498 — variant-scoped purposes need their field set on Shopify
        VARIANTS, not on the product. BLU-536 — gate widened from
        `design_variant` to the shared variant-scope set so every variant
        purpose gets the hint.
        BLU-1018 — two fixes. (1) Discovery now lists formal PRODUCTVARIANT
        definitions, so the old "variant metafields don't show up here" copy
        was WRONG; what still can't be harvested is a variant field with NO
        formal definition, because the informal harvest walks products only.
        (2) This used to render ONLY inside the `empty` branch, so the admin
        whose discovery SUCCEEDED — the normal case — was never told the
        purpose is variant-scoped. It now sits under the field in every
        state, which is also why the copy can no longer say "below": the
        inputs it used to point at are not always on screen.
      */}
      {VARIANT_PURPOSES.has(purpose) && (
        <p className="text-xs text-muted-foreground">
          Read from each product variant in Shopify, not from the product.
          Variant fields appear in this list once they have a definition, so
          add one for{' '}
          <span className="font-mono">{PURPOSE_KEY_PLACEHOLDER[purpose]}</span>{' '}
          or enter the namespace and key by hand.{' '}
          <a
            href="https://help.shopify.com/en/manual/custom-data/metafields/metafield-definitions/creating-custom-metafield-definitions"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-0.5 text-foreground underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded"
          >
            How to add metafields in Shopify{' '}
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </a>
        </p>
      )}
    </div>
  );
}

